import { type ComponentProps, type ReactNode } from "react";

import {
  ArrowRightIcon,
  ChevronDownIcon,
  Button,
  SearchField,
  SingleSelectDropdown,
  SingleSelectDropdownItem,
  cn,
} from "@stacklet/ui";
import pluralize from "pluralize";

import { XGridTable } from "./XGridTable";

/** Default selectable page sizes for the pagination footer */
const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;

/** Uppercases the first character of a string */
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Cursor-pagination state and handlers for the footer. Supplying this prop
 * renders the footer; omitting it hides pagination entirely (e.g. for a
 * row-grouped grid that shows every row at once).
 */
export interface StackletGridPagination {
  /** Current page size */
  pageSize: number;
  /** Selectable page sizes; defaults to 5/10/25/50/100 */
  pageSizeOptions?: readonly number[];
  /** Called when a new page size is chosen */
  onPageSizeChange: (size: number) => void;
  /** Whether a next page exists */
  hasNextPage: boolean;
  /** Whether a previous page exists */
  hasPreviousPage: boolean;
  /** Advance to the next page */
  onNextPage: () => void;
  /** Return to the previous page */
  onPreviousPage: () => void;
  /** 1-based index of the first row on the current page */
  rangeStart: number;
  /** 1-based index of the last row on the current page */
  rangeEnd: number;
  /** Total number of rows across all pages, when known */
  total?: number | null;
  /** Singular noun for the summary text, e.g. "user"; defaults to "item" */
  unit?: string;
  /** Disables the page controls (e.g. while a page is loading) */
  isDisabled?: boolean;
}

/**
 * Assembles the footer's {@link StackletGridPagination} from a
 * `useCursorPagination` result and the connection's page info — the boilerplate
 * every cursor-paginated grid would otherwise repeat.
 */
export function toGridPagination(
  cursor: {
    pageSize: number;
    isPending: boolean;
    rangeStart: number;
    rangeEnd: number;
    handleNext: () => void;
    handlePrevious: () => void;
    handlePageSizeChange: (size: number) => void;
  },
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    total?: number | null;
  },
  opts: { unit: string; pageSizeOptions?: readonly number[] },
): StackletGridPagination {
  return {
    pageSize: cursor.pageSize,
    pageSizeOptions: opts.pageSizeOptions,
    onPageSizeChange: cursor.handlePageSizeChange,
    hasNextPage: pageInfo.hasNextPage,
    hasPreviousPage: pageInfo.hasPreviousPage,
    onNextPage: cursor.handleNext,
    onPreviousPage: cursor.handlePrevious,
    rangeStart: cursor.rangeStart,
    rangeEnd: cursor.rangeEnd,
    total: pageInfo.total,
    unit: opts.unit,
    isDisabled: cursor.isPending,
  };
}

type XGridTableProps = ComponentProps<typeof XGridTable>;

/** Props for the StackletGrid component */
interface Props extends Omit<XGridTableProps, "customInline" | "pagination"> {
  /** Controlled value of the search field; omit to hide the search field */
  search?: string;
  /** Called on each search keystroke; required to show the search field */
  onSearchChange?: (value: string) => void;
  /** Placeholder text for the search field */
  searchPlaceholder?: string;
  /** Accessible label for the search field */
  searchAriaLabel?: string;
  /** Pagination state; supply to render the footer, omit to hide it */
  pagination?: StackletGridPagination;
  /** Escape hatch to render arbitrary content in the toolbar instead of search */
  customInline?: ReactNode;
  /** Drops the search field's box (bg + border) so it blends into the toolbar. */
  transparentSearch?: boolean;
}

/**
 * Shared browser-style data grid built on {@link XGridTable}. Owns the chrome
 * common to Stacklet's list views — a search field and export button in the
 * toolbar, and an optional external cursor-pagination footer — while leaving
 * data fetching to the caller. Any DataGrid prop (columns, rows, loading, row
 * grouping, density, …) passes straight through to the underlying grid.
 */
export function StackletGrid({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchAriaLabel = "Search",
  pagination,
  customInline,
  transparentSearch = false,
  slots,
  slotProps,
  ...rest
}: Props) {
  const searchField = onSearchChange ? (
    <SearchField
      aria-label={searchAriaLabel}
      // The wrapper div carries the box; drop it so search blends into the toolbar.
      className={
        transparentSearch
          ? "[&>div]:!border-transparent [&>div]:!bg-transparent [&>div]:hover:!ring-0"
          : undefined
      }
      inputProps={{ placeholder: searchPlaceholder }}
      onChange={onSearchChange}
      value={search ?? ""}
    />
  ) : null;

  // The toolbar carries a gray background by default; callers can extend (not
  // replace) it via slotProps.toolbar.className.
  const mergedSlotProps = {
    ...slotProps,
    toolbar: {
      ...slotProps?.toolbar,
      className: cn("bg-background-secondary", slotProps?.toolbar?.className),
    },
  };

  return (
    <div className="flex flex-col">
      <XGridTable
        {...rest}
        customInline={customInline ?? searchField}
        slotProps={mergedSlotProps}
        slots={{ footer: () => null, ...slots }}
      />
      {pagination ? <PaginationFooter {...pagination} /> : null}
    </div>
  );
}

/**
 * The cursor-pagination footer: a page-size dropdown, a 1-based range summary
 * and prev/next controls. StackletGrid renders it under the grid; also exported
 * so a grid can render one per section (e.g. a member list per expanded group).
 */
export function PaginationFooter({
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  rangeStart,
  rangeEnd,
  total,
  unit = "item",
  isDisabled = false,
}: StackletGridPagination) {
  return (
    <div className="mt-sm flex items-center justify-between text-label-small text-text-secondary">
      <div className="flex items-center gap-sm">
        <SingleSelectDropdown
          AnchorEle={
            <Button
              IconRight={<ChevronDownIcon size={12} />}
              aria-label={`${capitalize(pluralize(unit))} per page`}
              variant="tertiary"
            >
              {pageSize}
            </Button>
          }
          placement="top"
        >
          {pageSizeOptions.map((size) => (
            <SingleSelectDropdownItem
              isSelected={size === pageSize}
              key={size}
              onPress={() => onPageSizeChange(size)}
            >
              {size}
            </SingleSelectDropdownItem>
          ))}
        </SingleSelectDropdown>
        <span>{pluralize(unit)} per page</span>
      </div>

      {total != null ? (
        <span>
          {rangeStart}&ndash;{rangeEnd} of {total} {pluralize(unit, total)}
        </span>
      ) : null}

      <div className="flex items-center gap-xs">
        <Button
          IconLeft={<ArrowRightIcon className="rotate-180" size={16} />}
          aria-label="Previous page"
          isDisabled={!hasPreviousPage || isDisabled}
          onPress={onPreviousPage}
          variant="tertiary"
        />
        <Button
          IconLeft={<ArrowRightIcon size={16} />}
          aria-label="Next page"
          isDisabled={!hasNextPage || isDisabled}
          onPress={onNextPage}
          variant="tertiary"
        />
      </div>
    </div>
  );
}
